import LegalLayout from "@/components/LegalLayout";
import useSiteContent from "@/hooks/useSiteContent";

const TOC = [
    { id: "summary", label: "At-a-glance" },
    { id: "window", label: "1. 7-day refund window" },
    { id: "eligibility", label: "2. Eligibility" },
    { id: "no-refund", label: "3. When refunds are not available" },
    { id: "amc", label: "4. AMC refund terms" },
    { id: "process", label: "5. How to request a refund" },
    { id: "timeline", label: "6. Refund timeline" },
    { id: "cancellation", label: "7. Cancellation" },
    { id: "contact", label: "8. Contact" },
];

export default function Refund() {
    const { content } = useSiteContent();
    if (content.refund_html && content.refund_html.trim()) {
        return (
            <LegalLayout title="Refund &amp; Cancellation Policy" updated="Live · CMS" toc={TOC}>
                <div data-testid="refund-cms-body" dangerouslySetInnerHTML={{ __html: content.refund_html }} />
            </LegalLayout>
        );
    }
    return (
        <LegalLayout title="Refund &amp; Cancellation Policy" updated="February 2026" toc={TOC}>
            <p className="text-sm text-[#17212B]/70">
                Standard refund policy. Applies to all packages purchased on
                the Doctor Digital Identity Platform, operated by HUMANITYFIRST
                SOFTWARE TECHNOLOGY PRIVATE LIMITED.
            </p>

            <div id="summary" className="not-prose bg-[#0F766E]/5 border border-[#0F766E]/30 rounded-xl p-5 my-6">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">At-a-glance</div>
                <ul className="mt-3 space-y-2 text-sm text-[#17212B]">
                    <li>✓ <strong>7-day refund window</strong> on the advance payment, provided your public identity page has not yet been published.</li>
                    <li>✓ Refunds are made to the original payment method within <strong>5–7 business days</strong> of approval.</li>
                    <li>✗ Once the identity page is <strong>published</strong>, the advance payment is non-refundable.</li>
                    <li>✗ Balance (50%) payment is not refundable once made, as it unlocks publication and asset generation.</li>
                    <li>ℹ AMC has its own pro-rata refund rules — see section 4.</li>
                </ul>
            </div>

            <h2 id="window">1. 7-day refund window on advance payment</h2>
            <p>
                You may request a full refund of the <strong>advance (50%)
                payment</strong> within <strong>seven (7) calendar days</strong>
                {" "}of the payment date, provided that:
            </p>
            <ul>
                <li>Your public identity page has <strong>not yet been published</strong>, AND</li>
                <li>Onboarding has not been completed and submitted for admin approval, OR onboarding is still in an editable status (draft / onboarding / changes_requested).</li>
            </ul>

            <h2 id="eligibility">2. Eligibility</h2>
            <p>
                To be eligible, you must (a) be the account holder, (b) have paid
                the advance from a payment method registered in your own name,
                and (c) provide the invoice reference or payment order ID when
                you make the request.
            </p>

            <h2 id="no-refund">3. When refunds are NOT available</h2>
            <ul>
                <li><strong>After publication</strong> — once your public identity page is live at your <em>/dr/&lt;slug&gt;</em> URL, the advance payment is fully consumed (AI generation + admin review + rendering + hosting cost is non-reversible).</li>
                <li><strong>Balance (50%) payment</strong> — non-refundable once received (it unlocks publish, asset generation, and hosting).</li>
                <li><strong>After the 7-day window</strong> even if you have not yet published (as our AI processing and admin review costs will already have been incurred for jobs that ran).</li>
                <li><strong>Chargebacks &amp; disputes</strong> — please contact us before initiating a chargeback; disputed refunds are pursued through our grievance channel first.</li>
            </ul>

            <h2 id="amc">4. AMC refund terms</h2>
            <p>
                The Annual Maintenance Charge (AMC) begins after the first year of
                publication and covers hosting, security, and updates.
            </p>
            <ul>
                <li>You may cancel AMC any time; hosting and updates cease at the end of the current paid period.</li>
                <li>A pro-rata refund of the unused AMC period may be requested only within <strong>15 days</strong> of AMC renewal, calculated per whole unused month, minus a nominal admin fee.</li>
                <li>No refund on AMC after 15 days of renewal.</li>
            </ul>

            <h2 id="process">5. How to request a refund</h2>
            <ol>
                <li>Call our support number: <a href="tel:+917722090290">+91 7722090290</a> (Mon–Sat, 10:00–18:00 IST), OR</li>
                <li>Write to us at the registered address with subject line <em>&ldquo;Refund request &mdash; &lt;your registered email&gt;&rdquo;</em>.</li>
                <li>Include: your registered email, invoice number / order ID, payment date, and reason.</li>
            </ol>
            <p>We will acknowledge within 3 business days and communicate the outcome within 7 business days.</p>

            <h2 id="timeline">6. Refund timeline</h2>
            <p>
                Approved refunds are initiated within <strong>2 business days</strong>
                {" "}via the same payment method used for the original transaction.
                Banks / networks typically credit within <strong>5–7 business
                days</strong> of initiation. UPI/wallet refunds are usually faster;
                cards may take up to 10 business days depending on the issuing bank.
            </p>

            <h2 id="cancellation">7. Account cancellation</h2>
            <p>
                You may cancel your account at any time via in-app Settings.
                Cancellation stops future AMC billing but does not, by itself,
                trigger a refund — refunds follow the rules above. Once
                cancelled, your public identity page will be taken offline
                within 24 hours (redirecting to a &ldquo;Profile no longer
                available&rdquo; page) and your data handled per our{" "}
                <a href="/privacy">Privacy Policy</a>.
            </p>

            <h2 id="contact">8. Contact</h2>
            <p>
                HUMANITYFIRST SOFTWARE TECHNOLOGY PRIVATE LIMITED,
                <br />
                A-803 Palatial Height, Chandivali Farm Road, Andheri, Mumbai,
                Maharashtra 400072, India.
                <br />
                Phone: <a href="tel:+917722090290">+91 7722090290</a>. See also{" "}
                <a href="/contact">/contact</a>.
            </p>
        </LegalLayout>
    );
}
